import React from 'react';
import { Box, Button, Grid, Link, Paper, Typography } from '@mui/material';
import { COLOR_PRIMARY } from 'src/utils/const';
import {
  PricingAdvancedApiArt,
  PricingAdvancedEnterpriseArt,
  PricingAdvancedPropertyArt,
} from './PricingAdvancedSolutionsArt';

export interface PricingAdvancedSolutionsSectionProps {
  onEnterpriseContactClick: () => void;
}

const cardBodySx = {
  color: 'rgba(15, 23, 42, 0.78)',
  fontSize: '0.9375rem',
  lineHeight: 1.55,
  marginBottom: 2,
  flexGrow: 1,
};

const learnMoreBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 700,
  fontSize: '0.875rem',
  color: COLOR_PRIMARY,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

const baseCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  p: 2.5,
  borderRadius: '12px',
  bgcolor: '#ffffff',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 16px -4px rgba(15, 23, 42, 0.08)',
};

const titleSx = {
  fontWeight: 700,
  mb: 1,
  color: '#0f172a',
  fontSize: '1rem',
  lineHeight: 1.35,
};

const PricingAdvancedSolutionsSection = ({
  onEnterpriseContactClick,
}: PricingAdvancedSolutionsSectionProps) => (
  <Grid
    container
    item
    xs={12}
    sx={{
      maxWidth: '100%',
      backgroundColor: 'transparent',
      px: { xs: 1, sm: 2 },
      py: { xs: 1, md: 1.5 },
    }}
  >
    <Grid item xs={12} sx={{ mb: { xs: 2, md: 2.5 }, textAlign: 'center' }}>
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{
          color: 'rgba(15, 23, 42, 0.55)',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          fontSize: { xs: '0.9375rem', md: '1rem' },
        }}
      >
        Need more than a dashboard?
      </Typography>
    </Grid>

    <Grid container item spacing={2.5} justifyContent="center" alignItems="stretch">
      {/* API */}
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={baseCardSx}>
          <Box sx={{ mb: 1, maxHeight: 120, overflow: 'hidden' }}>
            <PricingAdvancedApiArt />
          </Box>
          <Typography variant="h6" component="h3" sx={titleSx}>
            API
          </Typography>
          <Typography sx={cardBodySx}>
            Plug Airbtics short-term rental data into your own dashboards, models, and products with
            a clean, documented API.
          </Typography>
          <Link
            href="https://airbtics.com/airbnb-api"
            target="_blank"
            rel="noopener noreferrer"
            sx={learnMoreBtnSx}
          >
            Learn more <span aria-hidden="true">→</span>
          </Link>
        </Paper>
      </Grid>

      {/* Enterprise — subtle highlight (same footprint as siblings) */}
      <Grid item xs={12} md={4}>
        <Paper
          elevation={0}
          sx={{
            ...baseCardSx,
            position: 'relative',
            overflow: 'hidden',
            border: `1px solid rgba(58, 175, 169, 0.35)`,
            background:
              'linear-gradient(180deg, rgba(58, 175, 169, 0.09) 0%, #ffffff 42%, #ffffff 100%)',
            boxShadow:
              '0 1px 2px rgba(58, 175, 169, 0.06), 0 6px 20px -8px rgba(58, 175, 169, 0.22)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${COLOR_PRIMARY} 0%, #007888 100%)`,
            },
          }}
        >
          <Box sx={{ mb: 1, maxHeight: 120, overflow: 'hidden' }}>
            <PricingAdvancedEnterpriseArt />
          </Box>
          <Typography variant="h6" component="h3" sx={titleSx}>
            Enterprise Solutions
          </Typography>
          <Typography sx={cardBodySx}>
            Tailored enterprise solutions for companies needing custom data coverage, special
            delivery workflows, or commercial use rights. Designed for higher-scale,
            business-critical use cases.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto' }}>
            <Link
              href="https://airbtics.com/airbnb-data/"
              target="_blank"
              rel="noopener noreferrer"
              sx={learnMoreBtnSx}
            >
              Learn more <span aria-hidden="true">→</span>
            </Link>
            <Button
              variant="outlined"
              size="small"
              onClick={onEnterpriseContactClick}
              sx={{
                alignSelf: 'flex-start',
                borderRadius: '50px',
                px: 2,
                py: 0.75,
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                borderColor: COLOR_PRIMARY,
                color: '#0f172a',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                '&:hover': {
                  borderColor: '#007888',
                  backgroundColor: '#fff',
                },
              }}
            >
              Contact us
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* Property-level */}
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={baseCardSx}>
          <Box sx={{ mb: 1, maxHeight: 120, overflow: 'hidden' }}>
            <PricingAdvancedPropertyArt />
          </Box>
          <Typography variant="h6" component="h3" sx={titleSx}>
            Property-level Performance Data
          </Typography>
          <Typography sx={cardBodySx}>
            Ready-to-use property-level Airbnb performance data for internal analysis, research, and
            model training. Explore coverage, sample files, data dictionary, pricing, and update
            options before you buy.
          </Typography>
          <Link
            href="https://airbtics.com/airbnb-dataset"
            target="_blank"
            rel="noopener noreferrer"
            sx={learnMoreBtnSx}
          >
            Learn more <span aria-hidden="true">→</span>
          </Link>
        </Paper>
      </Grid>
    </Grid>
  </Grid>
);

export default PricingAdvancedSolutionsSection;
